import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import type { ReactNode } from 'react'
import { Fragment, useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import type {
  HotelMapViewData,
  ExperienceMapViewData,
  TripItineraryData,
  TripStop,
  TripDay,
  TripItineraryItem,
  TripItemWithoutDay,
} from 'tripPlanner/api/types'
import TripPlanCanvasItem from './TripPlanCanvasItem'
import TripPlanOriginStop from './TripPlanOriginStop'
import TripPlanTransportLine from './TripPlanTransportLine'
import type { ItineraryScrollTarget } from '../hooks/useItineraryUpdates'
import { CANVAS_CONTENT_WIDTH } from './constants'

export type Highlight = {
  id: number
  name: string
  emoji: string
  description: string
  googlePlaceId?: string
  savedItemData?: TripItemWithoutDay['savedItemData']
}

const ScrollableContainer = styled(Sheet)`
  height: 100%;
  overflow-y: auto;
`

const SkeletonStopSheet = styled(Sheet)`
  width: ${rem(CANVAS_CONTENT_WIDTH)};
  border-radius: ${props => props.theme.borderRadius.L};
`

const DottedConnector = styled.div`
  width: ${rem(CANVAS_CONTENT_WIDTH)};
  height: ${rem(24)};
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: ${rem(15)};
    top: 0;
    bottom: 0;
    border-left: ${rem(2)} dashed ${props => props.theme.palette.neutral.default.five};
  }
`

const ItemSpacer = styled.div`
  height: ${rem(40)};
`

interface Props {
  data?: TripItineraryData
  onLearnMoreClick?: (googlePlaceId: string, placeName: string, countryName: string) => void
  onRefetchItinerary?: () => void
  onDayDeleted?: () => void
  onSendMessage?: (message: string) => void
  isStreaming?: boolean
  expandedStopIds?: Set<number>
  onToggleStopExpanded?: (stopId: number, expanded: boolean) => void
  onExploreMap: (coordinates: { lat: number; lng: number }) => void
  onShowMap: (data: HotelMapViewData) => void
  onShowExperienceOnMap?: (data: ExperienceMapViewData) => void
  enrichingStopIds?: Set<number>
  onAskAI?: (dayNumber: number, dayTitle: string, date?: string) => void
  scrollTarget?: ItineraryScrollTarget | null
  onScrollComplete?: () => void
}

function TripPlanCanvas(props: Props) {
  const { data, onLearnMoreClick, onRefetchItinerary, onDayDeleted, onSendMessage, isStreaming, expandedStopIds, onToggleStopExpanded, onExploreMap, onShowMap, onShowExperienceOnMap, enrichingStopIds, onAskAI, scrollTarget, onScrollComplete } = props

  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const stops = useMemo(() => data?.stops ?? [], [data])

  const highlightsByStop = useMemo(() => {
    return stops.reduce<Record<number, Array<Highlight>>>((acc: Record<number, Array<Highlight>>, stop: TripStop) => {
      const highlights: Array<Highlight> = []

      stop.itemsWithoutDay?.forEach((item: TripItemWithoutDay) => {
        if (item.isHighlight) {
          highlights.push({
            id: item.id,
            name: item.name,
            emoji: item.emoji,
            description: item.description,
            googlePlaceId: item.itemPlaceDetail?.placeId,
            savedItemData: item.savedItemData,
          })
        }
      })

      stop.days.forEach((day: TripDay) => {
        day.items.forEach((item: TripItineraryItem) => {
          if (item.isHighlight) {
            highlights.push({
              id: item.id,
              name: item.name,
              emoji: item.emoji,
              description: item.description,
              googlePlaceId: item.itemPlaceDetail?.placeId,
              savedItemData: item.savedItemData,
            })
          }
        })
      })

      const hasDays = stop.days.length > 0
      const hasItemsWithoutDay = (stop.itemsWithoutDay?.length ?? 0) > 0
      if (highlights.length === 0 && !hasDays && hasItemsWithoutDay) {
        stop.itemsWithoutDay?.forEach((item: TripItemWithoutDay) => {
          highlights.push({
            id: item.id,
            name: item.name,
            emoji: item.emoji,
            description: item.description,
            googlePlaceId: item.itemPlaceDetail?.placeId,
            savedItemData: item.savedItemData,
          })
        })
      }

      if (highlights.length > 0) {
        acc[stop.id] = highlights
      }
      return acc
    }, {})
  }, [stops])

  const enrichedStops = useMemo(() => {
    return stops.map((stop: TripStop) => {
      const stopHighlights = highlightsByStop[stop.id] || []
      const numberOfDays = stop.days?.length ?? 0

      return {
        stop,
        stopHighlights,
        numberOfDays,
      }
    })
  }, [stops, highlightsByStop])

  useEffect(() => {
    if (scrollTarget == null || !scrollContainerRef.current) return

    let rafId: number
    let retryCount = 0
    const maxRetries = 5

    const attemptScroll = () => {
      const container = scrollContainerRef.current
      if (!container) {
        onScrollComplete?.()
        return
      }

      if (scrollTarget.itemId !== undefined) {
        const itemEl = container.querySelector(`div[data-item-id="${scrollTarget.itemId}"]`)
        if (itemEl) {
          itemEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
          onScrollComplete?.()
          return
        }
        if (retryCount < maxRetries) {
          retryCount++
          rafId = requestAnimationFrame(attemptScroll)
          return
        }
      }

      const stopEl = container.querySelector(`[data-stop-id="${scrollTarget.stopId}"]`)
      if (stopEl) {
        stopEl.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
      onScrollComplete?.()
    }

    rafId = requestAnimationFrame(attemptScroll)

    return () => {
      cancelAnimationFrame(rafId)
    }
  }, [scrollTarget, onScrollComplete])

  const hasStops = stops.length > 0

  if (isStreaming && !hasStops) {
    return (
      <ScrollableContainer padding={40}>
        <Group direction="vertical" gap={40} horizontalAlign="center">
          <SkeletonStopSheet padding={20} border="neutral-five">
            <Group direction="vertical" gap={20}>
              <Group direction="vertical" gap={12}>
                <Group direction="horizontal" gap={12} verticalAlign="start">
                  <LoadingBox width={56} height={56} borderRadius="S" />
                  <Group direction="vertical" gap={4}>
                    <LoadingBox width={160} height={24} borderRadius="XS" />
                    <LoadingBox width={60} height={18} borderRadius="XS" />
                  </Group>
                </Group>
                <Group direction="vertical" gap={8}>
                  {Array.from({ length: 3 }).map((_, index) => (
                    <Fragment key={index}>
                      <LoadingBox width={220} height={16} borderRadius="XS" />
                      <LoadingBox width={600} height={16} borderRadius="XS" />
                    </Fragment>
                  ))}
                </Group>
              </Group>
            </Group>
          </SkeletonStopSheet>
        </Group>
      </ScrollableContainer>
    )
  }

  return (
    <ScrollableContainer padding={40} ref={scrollContainerRef}>
      <Group direction="vertical" gap={0} horizontalAlign="center">
        {enrichedStops.map(({ stop, stopHighlights, numberOfDays }, index) => {
          const isOrigin = stop.stopType === 'ORIGIN'
          const transports = stop.transports
          const hasTransport = transports?.some((t) => t.options.length > 0)

          const prevStop = index > 0 ? enrichedStops[index - 1].stop : undefined
          const prevHadTransport = prevStop?.transports?.some((t) => t.options.length > 0) ?? false

          const hasStopContent = (stop.days?.length ?? 0) > 0 || (stop.itemsWithoutDay?.length ?? 0) > 0 || (stopHighlights.length > 0)

          let stopContent: ReactNode
          if (isOrigin && !hasStopContent) {
            stopContent = <TripPlanOriginStop
              stopId={stop.id}
              placeName={stop.placeName}
              placeCountry={stop.placeCountry}
              countryFlag={stop.countryFlag}
              onRefetchItinerary={onRefetchItinerary}
            />
          } else {
            stopContent = <TripPlanCanvasItem
              data={data}
              stopId={stop.id}
              placeName={stop.placeName}
              placeCountry={stop.placeCountry}
              numberOfDays={numberOfDays}
              description={stop.description}
              imageId={stop.imageIds?.[0] ?? ''}
              stopSeq={stop.stopSeq}
              highlights={stopHighlights}
              googlePlaceId={stop.stopPlaceDetail?.placeId}
              onLearnMoreClick={onLearnMoreClick}
              onRefetchItinerary={onRefetchItinerary}
              onDayDeleted={onDayDeleted}
              onSendMessage={onSendMessage}
              isStreaming={isStreaming}
              isExpanded={expandedStopIds?.has(stop.id)}
              onToggleExpanded={onToggleStopExpanded}
              onExploreMap={onExploreMap}
              onShowMap={onShowMap}
              onShowExperienceOnMap={onShowExperienceOnMap}
              stopCoordinates={stop.stopPlaceDetail ? { lat: stop.stopPlaceDetail.lat, lng: stop.stopPlaceDetail.lng } : undefined}
              isEnriching={enrichingStopIds?.has(stop.id)}
              onAskAI={onAskAI}
              scrollTargetDayId={scrollTarget?.stopId === stop.id ? scrollTarget.dayId : undefined}
            />
          }

          return (
            <Fragment key={stop.id}>
              {index > 0 && prevHadTransport && <DottedConnector />}
              {index > 0 && !prevHadTransport && <ItemSpacer />}
              <div data-stop-id={stop.id}>
                {stopContent}
              </div>
              {hasTransport && transports?.map((transport) =>
                transport.options.length > 0 && (
                  <Fragment key={`${transport.departureStopId}-${transport.arrivalStopId}`}>
                    <DottedConnector />
                    <TripPlanTransportLine transport={transport} onRefetchItinerary={onRefetchItinerary} onSendMessage={onSendMessage} />
                  </Fragment>
                ),
              )}
            </Fragment>
          )
        })}
      </Group>
    </ScrollableContainer>
  )
}

export default TripPlanCanvas
